import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '../../../../components';
import type { StatsDelta } from '../../../../mock-data/types';
import { Delta, LiveNumber } from '../../../stats/components';
import { formatCount } from '../../format';
import { Tile, Label, Figure, ChangeRow, Caption, Hint } from './KpiTile.styles';

export type KpiFigure = {
  value: number;
  /** A currency mark before the number ("$"). */
  prefix?: string;
  suffix?: string;
  decimals?: number;
};

/** The suspected subset of a tile's figure: a count, or an average time in seconds read in the figure's own unit. */
export type KpiSuspected = { count: number } | { seconds: number; unit: 'sec' | 'min' };

export type KpiTileProps = {
  label: string;
  /** One plain sentence saying what the figure means. */
  hint: string;
  /** Null while there is nothing to show (no visits measured). */
  figure: KpiFigure | null;
  /** The change against the period before; null hides it (no earlier figure to compare with). */
  change: StatsDelta | null;
  caption: string;
  loading?: boolean;
  error?: boolean;
  /** A darker, recessed tile (a second group of figures on the same card). */
  sunken?: boolean;
  /** The suspected subset under the figure; left out while the figure is unavailable, loading or failed. */
  suspected?: KpiSuspected | null;
};

/** The suspected line's key and its `{{n}}`: a time reads in the figure's unit (sec under one minute, else min). */
const suspectedLine = (s: KpiSuspected, language: string, unit: (u: 'sec' | 'min') => string): [string, string] => {
  if ('count' in s) return ['Includes {{n}} suspected', formatCount(s.count, language)];
  if (s.unit === 'sec') return ['Suspected: {{n}}', `${formatCount(s.seconds, language)} ${unit('sec')}`];
  const minutes = new Intl.NumberFormat(language || undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.round((s.seconds / 60) * 10) / 10);
  return ['Suspected: {{n}}', `${minutes} ${unit('min')}`];
};

/** One headline figure of the Website tab (a sub-card of the overview card): what it is, the number, how it moved, and a one-line explanation. */
export const KpiTile = ({
  label,
  hint,
  figure,
  change,
  caption,
  loading = false,
  error = false,
  sunken = false,
  suspected = null,
}: KpiTileProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const line =
    suspected && figure !== null && !loading && !error ? suspectedLine(suspected, i18n.language, (u) => t(u)) : null;
  return (
    <Tile $sunken={sunken}>
      <Label text={label} tag="span" raw />
      <Figure>
        <LiveNumber
          value={figure?.value ?? 0}
          prefix={figure?.prefix}
          suffix={figure?.suffix}
          decimals={figure?.decimals ?? 0}
          loading={loading}
          error={error || undefined}
          indeterminate={(!loading && !error && figure === null) || undefined}
        />
      </Figure>
      {line ? <Caption text={line[0]} placeholders={{ n: line[1] }} tag="span" /> : null}
      <ChangeRow>
        {loading ? (
          <Skeleton width={56} height={18} radius={6} />
        ) : change ? (
          <>
            <Delta delta={change} size={13} />
            <Caption text={caption} tag="span" raw />
          </>
        ) : null}
      </ChangeRow>
      <Hint text={hint} tag="span" raw />
    </Tile>
  );
};

export default KpiTile;
